/**
 * Squares are 0x88 indices: `rank * 16 + file`, with file 0 = a and rank 0 = 1.
 *
 * The upside of 0x88 over a plain 0..63 board is that off-board detection is a
 * single mask — `index & 0x88` is non-zero exactly when a ray has walked off the
 * edge — so sliding piece generation needs no bounds arithmetic at all.
 */
export type Square = number & { readonly __square: unique symbol }

export type FileName = 'a' | 'b' | 'c' | 'd' | 'e' | 'f' | 'g' | 'h'
export type RankName = '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8'

/**
 * `"e4"` type-checks, `"e9"` does not. Test fixtures written against this type
 * catch their own typos at compile time.
 */
export type SquareName = `${FileName}${RankName}`

export const FILE_NAMES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'] as const
export const RANK_NAMES = ['1', '2', '3', '4', '5', '6', '7', '8'] as const

export const BOARD_SIZE = 128

export const square = (file: number, rank: number): Square => ((rank << 4) | file) as Square

export const fileOf = (sq: Square): number => sq & 0xf
export const rankOf = (sq: Square): number => sq >> 4

export const isOnBoard = (index: number): boolean => (index & 0x88) === 0

/** Walk a ray one step. Returns `null` the moment it leaves the board. */
export const step = (sq: Square, delta: number): Square | null => {
  const next = sq + delta
  return isOnBoard(next) ? (next as Square) : null
}

export const squareName = (sq: Square): SquareName =>
  `${FILE_NAMES[fileOf(sq)] as FileName}${RANK_NAMES[rankOf(sq)] as RankName}`

/**
 * The compile-time-checked constructor. Use this in fixtures and tests:
 * `sq('e4')` is a `Square`, `sq('e9')` is a type error.
 */
export const sq = (name: SquareName): Square => {
  const file = FILE_NAMES.indexOf(name[0] as FileName)
  const rank = RANK_NAMES.indexOf(name[1] as RankName)
  return square(file, rank)
}

/** The runtime counterpart, for parsing FEN/SAN where the input is arbitrary. */
export const parseSquare = (name: string): Square | null => {
  if (name.length !== 2) return null
  const file = FILE_NAMES.indexOf(name[0] as FileName)
  const rank = RANK_NAMES.indexOf(name[1] as RankName)
  if (file === -1 || rank === -1) return null
  return square(file, rank)
}

/** All 64 playable squares, a1..h8 in rank-major order. */
export const ALL_SQUARES: readonly Square[] = Array.from({ length: BOARD_SIZE }, (_, i) => i)
  .filter(isOnBoard)
  .map((i) => i as Square)

/** 0..63 index, for UI grids and any consumer that would rather not know about 0x88. */
export const toIndex64 = (s: Square): number => (rankOf(s) << 3) | fileOf(s)
export const fromIndex64 = (index: number): Square => square(index & 7, index >> 3)

/** Light squares are the ones where file and rank have the same parity. */
export const isLightSquare = (s: Square): boolean => ((fileOf(s) + rankOf(s)) & 1) === 1
